import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import getConditionChildrenKey from 'editor/src/store/design/reducer/getConditionChildrenKey';
import { importConditionAction, importConditionLinkedContentAction } from 'editor/src/store/design/slice';
import {
  Condition,
  ConditionAddress,
  ConditionChild,
  ConditionOption,
  MediaElement,
} from 'editor/src/store/design/types';
import {
  ExportedConditionDataV2,
  ExportedConditionElement,
  ExportedConditionFile,
  ExportedConditionFileV2,
} from 'editor/src/store/design/util/conditionTransfer';
import fitImportedElementsToPage from 'editor/src/store/design/util/fitImportedElementsToPage';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import loadFontOperation from 'editor/src/store/fonts/operation/loadFontOperation';
import type { Thunk } from 'editor/src/store/hooks';

import { conditionOptionUuids, conditionUuids, elementUuids } from 'editor/src/util/uuids';

import applyTextVariantsForConditionOperation from './applyTextVariantsForConditionOperation';
import updateHiddenElementsOperation from './updateHiddenElementsOperation';

/** A validated import file, tagged with the version validateImportedConditionFile resolved it to. */
export type ImportedConditionFile =
  | { version: ExportedConditionFile['version']; condition: ExportedConditionFile['condition'] }
  | {
      version: ExportedConditionFileV2['version'];
      condition: ExportedConditionDataV2;
      elements: ExportedConditionElement[];
    };

interface LinkedContentPlan {
  conditionsToInsert: Condition[];
  bucketChildren: { bucketKey: string; children: ConditionChild[] }[];
  newElements: MediaElement[];
  topBucketAdditions: ConditionChild[];
}

function clampIndex(index: number, length: number): number {
  return Math.min(Math.max(index, 0), length - 1);
}

/**
 * Builds everything a v2 import needs to create, purely from the validated
 * file plus the freshly generated ids for the TARGET condition's own new
 * options (already produced by importConditionOperation for the v1 replace).
 * Every id below is generated here — a v2 file carries none of its own, see
 * conditionTransfer.
 */
function buildLinkedContentPlan(
  rootConditionId: string,
  rootOptionIds: string[],
  rootExported: ExportedConditionDataV2,
  exportedElements: ExportedConditionElement[],
): LinkedContentPlan {
  const conditionsToInsert: Condition[] = [];
  const bucketChildren: { bucketKey: string; children: ConditionChild[] }[] = [];
  const pathToTarget = new Map<string, { id: string; optionIds: string[] }>();
  const newUuidByElementIndex = new Map<number, number>();

  function getOrCreateUuid(index: number): number {
    const existing = newUuidByElementIndex.get(index);
    if (existing !== undefined) {
      return existing;
    }
    const uuid = elementUuids.generate();
    newUuidByElementIndex.set(index, uuid);
    return uuid;
  }

  function walk(
    exported: ExportedConditionDataV2,
    ownId: string,
    ownOptionIds: string[],
    path: string,
    parent: Condition['parent'],
  ) {
    pathToTarget.set(path, { id: ownId, optionIds: ownOptionIds });

    if (path !== '') {
      const activeIndex = clampIndex(exported.defaultOptionIndex, ownOptionIds.length);
      conditionsToInsert.push({
        id: ownId,
        parent,
        name: exported.name,
        description: exported.description,
        showOnAllSpreads: exported.showOnAllSpreads,
        displayOption: exported.displayOption,
        options: ownOptionIds.map((id, index) => ({
          id,
          label: exported.options[index].label,
          imageId: exported.options[index].imageId,
        })),
        activeOptionId: ownOptionIds[activeIndex],
      });
    }

    exported.options.forEach((option, optionIndex) => {
      const bucketKey = getConditionChildrenKey(ownId, ownOptionIds[optionIndex]);
      const children: ConditionChild[] = [];

      option.elements.forEach((elementIndex) => {
        const exportedElement = exportedElements[elementIndex];
        const type = exportedElement?.element.type as MediaElement['type'] | undefined;
        if (!type) {
          return;
        }
        children.push({ type, id: getOrCreateUuid(elementIndex) });
      });

      option.conditions.forEach((subCondition, subIndex) => {
        const subId = conditionUuids.generate();
        const subOptionIds = subCondition.options.map(() => conditionOptionUuids.generate());
        const subPath = `${path ? `${path}.` : ''}${optionIndex}.${subIndex}`;
        walk(subCondition, subId, subOptionIds, subPath, {
          conditionId: ownId,
          optionIds: [ownOptionIds[optionIndex]],
        });
        children.push({ type: 'condition', id: subId });
      });

      bucketChildren.push({ bucketKey, children });
    });
  }

  walk(rootExported, rootConditionId, rootOptionIds, '', undefined);

  const newElements: MediaElement[] = [];
  const topBucketAdditions: ConditionChild[] = [];

  exportedElements.forEach((exportedElement, index) => {
    const type = exportedElement.element.type as MediaElement['type'] | undefined;
    if (!type) {
      return;
    }
    const uuid = getOrCreateUuid(index);
    const element = { ...exportedElement.element, uuid } as unknown as MediaElement;

    if (exportedElement.follows) {
      const target = pathToTarget.get(exportedElement.follows.condition);
      if (target && element.type === 'text') {
        if (exportedElement.follows.useOptionLabel) {
          element.extra = {
            ...element.extra,
            textVariants: { conditionId: target.id, useOptionLabel: true, values: {} },
          };
        } else {
          const values: Record<string, { text: string; fontFamily?: string }> = {};
          (exportedElement.follows.values ?? []).forEach((value, optionIndex) => {
            const optionId = target.optionIds[optionIndex];
            if (value && optionId) {
              values[optionId] = { text: value.text, ...(value.fontFamily ? { fontFamily: value.fontFamily } : {}) };
            }
          });
          element.extra = { ...element.extra, textVariants: { conditionId: target.id, values } };
        }
      }
    }

    newElements.push(element);
    if (exportedElement.personalized) {
      topBucketAdditions.push({ type, id: uuid });
    }
  });

  return { conditionsToInsert, bucketChildren, newElements, topBucketAdditions };
}

/**
 * Replaces a condition's name/description/display settings and its whole
 * option set with an already-validated imported file (see
 * validateImportedConditionFile). The condition keeps its id and position;
 * every option gets a freshly generated id — an imported file never carries
 * ids that could collide with this design's.
 *
 * A v2 file additionally recreates everything that was linked to the
 * exported condition — sub-conditions, the elements shown per option, and any
 * text elsewhere on the spread that followed it — inside the SAME undo step,
 * with brand-new ids throughout (see buildLinkedContentPlan).
 *
 * Confirmation (when anything would be lost) is the caller's job — see
 * getConditionImportImpact and the menu component. This operation always
 * performs the replace; it is one undo step.
 */
const importConditionOperation =
  (address: ConditionAddress, imported: ImportedConditionFile): Thunk =>
  (dispatch, getState) => {
    const options: ConditionOption[] = imported.condition.options.map((option) => ({
      id: conditionOptionUuids.generate(),
      label: option.label,
      imageId: option.imageId,
    }));
    const clampedIndex = clampIndex(imported.condition.defaultOptionIndex, options.length);
    const activeOptionId = options[clampedIndex].id;

    let plan: LinkedContentPlan | undefined;
    if (imported.version === 2) {
      plan = buildLinkedContentPlan(
        address.conditionId,
        options.map((option) => option.id),
        imported.condition,
        imported.elements,
      );
      const page = getState().design.designData?.spreads[address.spreadIndex]?.pages[0];
      if (page) {
        plan.newElements = fitImportedElementsToPage(plan.newElements, page.width, page.height);
      }
    }

    batch(() => {
      dispatch(saveUndoRedoStateOperation('Import condition'));
      dispatch(
        importConditionAction({
          address,
          name: imported.condition.name,
          description: imported.condition.description,
          displayOption: imported.condition.displayOption,
          showOnAllSpreads: imported.condition.showOnAllSpreads,
          options,
          activeOptionId,
        }),
      );

      if (plan) {
        dispatch(importConditionLinkedContentAction({ address, ...plan }));
      }

      // A useOptionLabel text was left following the condition (see
      // importConditionReducer) but its rendered text is still the OLD
      // option's label until re-applied — same as any other active-option
      // change. This also applies the active option of every sub-condition a
      // v2 import just created, since each starts at its own defaultOptionIndex.
      const affectedSpreads = new Set<number>();
      dispatch(applyTextVariantsForConditionOperation(address.conditionId, activeOptionId)).forEach((spreadIndex) =>
        affectedSpreads.add(spreadIndex),
      );
      plan?.conditionsToInsert.forEach((condition) => {
        dispatch(applyTextVariantsForConditionOperation(condition.id, condition.activeOptionId)).forEach(
          (spreadIndex) => affectedSpreads.add(spreadIndex),
        );
      });
      if (!affectedSpreads.has(address.spreadIndex)) {
        dispatch(updateHiddenElementsOperation(address.spreadIndex));
      }

      // Fonts for every imported text's BASE face — a variant swap above
      // already loads whichever face the active option introduced.
      const fontFamilies = new Set(
        (plan?.newElements ?? [])
          .filter((element): element is Extract<MediaElement, { type: 'text' }> => element.type === 'text')
          .map((element) => element.extra.fontFamily),
      );
      fontFamilies.forEach((fontFamily) => dispatch(loadFontOperation(fontFamily)));
    });
  };

export default importConditionOperation;
